/** The minute range of one open window; `WeeklyPeriod` and holiday/exception periods both fit. */
export interface MinuteRange {
  openMinute: number;
  closeMinute: number;
}

/** A window that ends at or before it starts. Its meaning (typo vs. overnight) is undecided, so it's left untouched. */
function isBackwards(p: MinuteRange): boolean {
  return p.closeMinute <= p.openMinute;
}

/**
 * Collapse one day's windows into continuous open blocks, sorted by open time.
 * Overlapping windows (9-5 + 2-6) and back-to-back ones (9-12 + 12-5) become a
 * single block (9-6, 9-5), so "when do we close" reads the real end of the
 * block rather than the end of whichever window started first. Split shifts
 * with a gap (9-12 + 1-5) stay separate.
 *
 * Backwards windows are passed through unmerged so their behavior doesn't
 * change here.
 */
export function mergePeriods(periods: MinuteRange[]): MinuteRange[] {
  const sorted = [...periods].sort((a, b) => a.openMinute - b.openMinute);
  const merged: MinuteRange[] = [];
  const backwards: MinuteRange[] = [];
  for (const p of sorted) {
    if (isBackwards(p)) {
      backwards.push({ openMinute: p.openMinute, closeMinute: p.closeMinute });
      continue;
    }
    const last = merged[merged.length - 1];
    if (last && p.openMinute <= last.closeMinute) {
      last.closeMinute = Math.max(last.closeMinute, p.closeMinute);
    } else {
      merged.push({ openMinute: p.openMinute, closeMinute: p.closeMinute });
    }
  }
  return [...merged, ...backwards].sort((a, b) => a.openMinute - b.openMinute);
}

/**
 * True when any two of one day's windows genuinely overlap (one starts before
 * another ends). Back-to-back windows (9-12 + 12-5) and split shifts with a gap
 * are allowed. Backwards windows are ignored.
 */
export function periodsOverlap(periods: MinuteRange[]): boolean {
  const sorted = periods.filter((p) => !isBackwards(p)).sort((a, b) => a.openMinute - b.openMinute);
  let latestClose = -Infinity;
  for (const p of sorted) {
    if (p.openMinute < latestClose) return true;
    latestClose = Math.max(latestClose, p.closeMinute);
  }
  return false;
}

/** True when any single day of a weekly schedule has overlapping windows. */
export function weeklyPeriodsOverlap(weekly: (MinuteRange & { dayOfWeek: number })[]): boolean {
  return [0, 1, 2, 3, 4, 5, 6].some((dow) => periodsOverlap(weekly.filter((p) => p.dayOfWeek === dow)));
}
